import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { AlertTriangle, Wallet, TrendingUp, Users, Activity, RefreshCw, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { insightsAPI } from '../services/api';
import useDarkReady from '../theme/useDarkReady';

// Admin Insights: what needs action. Spec: docs/superpowers/specs/2026-09-28-insights-design.md
const inr = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;
const inrK = (n) => (n >= 100000 ? `₹${(n / 100000).toFixed(1)}L` : n >= 1000 ? `₹${Math.round(n / 1000)}k` : `₹${Math.round(n || 0)}`);
const dayLabel = (d) => new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
const timeLabel = (t) => new Date(t).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' });
const mins = (m) => (m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`);
const SERVICE_TABS = [['all', 'All'], ['catv', 'Cable TV'], ['bb', 'Broadband']];
const SIGNAL_PILL = { dropping: ['badge-danger', 'Dropping out'], weak: ['badge-warning', 'Weak'], falling: ['badge-info', 'Falling'] };
const OUTAGE_LABEL = { pon_port_down: 'PON down', olt_offline: 'OLT offline', uplink_down: 'Uplink down' };

// Loads one section. A sequence number drops responses that arrive after a newer request
// (e.g. switching All → Broadband → Cable TV quickly), so a tab never shows another tab's data.
function useLoad(fn, deps) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const seq = useRef(0);
  const load = useCallback((...args) => {
    const mine = ++seq.current;
    setState((st) => ({ ...st, loading: true }));
    fn(...args).then((r) => { if (mine === seq.current) setState({ loading: false, data: r.data.data, error: null }); })
      .catch((e) => { if (mine === seq.current) setState({ loading: false, data: null, error: e.response?.data?.message || e.message }); });
  }, deps);
  useEffect(() => { load(); }, [load]);
  return [state, load];
}

function Section({ title, hint, right, children }) {
  return (
    <div className="card">
      <div className="card-header" style={{ alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h3>{title}</h3>
          {hint && <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginTop: 2, maxWidth: 620 }}>{hint}</div>}
        </div>
        {right}
      </div>
      <div className="card-body">{children}</div>
    </div>
  );
}

function StateLine({ state, empty, children }) {
  if (state.loading && !state.data) return <div style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>Loading…</div>;
  if (state.error) return <div style={{ color: 'var(--danger)', fontSize: '.85rem' }}>Couldn't load this section: {state.error}</div>;
  if (empty) return <div className="empty-state" style={{ padding: 20 }}><p>{empty}</p></div>;
  return <div style={{ opacity: state.loading ? 0.55 : 1, transition: 'opacity .15s' }} aria-busy={state.loading}>{children}</div>;
}

function Tile({ icon: Icon, label, value, foot }) {
  return (
    <div className="stat-card stat-card-compact">
      <div className="stat-icon" style={{ background: 'var(--primary-light)' }}><Icon size={16} color="var(--primary)" /></div>
      <div className="stat-info">
        <p>{label}</p>
        <h3>{value}</h3>
        <div className="trend">{foot}</div>
      </div>
    </div>
  );
}

function Spark({ points }) {
  const W = 120, H = 32, lo = -30, hi = -15;
  if (points.length < 2) return <span style={{ color: 'var(--text-muted)', fontSize: '.75rem' }}>not enough data</span>;
  const x = (i) => 3 + (i * (W - 6)) / (points.length - 1);
  const y = (v) => 3 + ((H - 6) * (hi - Math.max(lo, Math.min(hi, v)))) / (hi - lo);
  const line = points.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.rx).toFixed(1)}`).join(' ');
  const last = points[points.length - 1].rx;
  const col = last <= -27 ? 'var(--danger)' : last <= -25 ? 'var(--warning)' : 'var(--primary)';
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true" style={{ display: 'block' }}>
      <line x1="0" x2={W} y1={y(-27)} y2={y(-27)} stroke="var(--danger)" strokeDasharray="3 3" opacity=".5" />
      <path d={line} fill="none" stroke={col} strokeWidth="2" strokeLinejoin="round" />
      <circle cx={x(points.length - 1)} cy={y(last)} r="3" fill={col} />
    </svg>
  );
}

function DepositModal({ row, onClose, onSaved }) {
  const [amount, setAmount] = useState(String(Math.max(0, Math.round(row.cashInHand ?? row.cash ?? 0))));
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [recent, setRecent] = useState([]);
  const [confirmId, setConfirmId] = useState(null);
  const loadRecent = useCallback(() => {
    insightsAPI.deposits(row.agent.id).then((r) => setRecent(r.data.data)).catch(() => setRecent([]));
  }, [row.agent.id]);
  useEffect(() => { loadRecent(); }, [loadRecent]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  const remove = async (id) => {
    try {
      await insightsAPI.deleteDeposit(id);
      toast.success('Deposit removed');
      setConfirmId(null);
      loadRecent();
      onSaved(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not remove the deposit');
    }
  };
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await insightsAPI.recordDeposit({ agent: row.agent.id, amount: Number(amount), notes: notes || undefined });
      toast.success('Deposit recorded');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not record the deposit');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <form className="modal" role="dialog" aria-modal="true" aria-labelledby="dep-title" style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()} onSubmit={save}>
        <div className="modal-header"><h3 id="dep-title">Record deposit · {row.agent.name}</h3><button type="button" className="modal-close" aria-label="Close" onClick={onClose}><X size={16} /></button></div>
        <div className="modal-body">
          {row.cashInHand == null && (
            <p className="form-hint" style={{ marginTop: 0, marginBottom: 14 }}>
              This is {row.agent.name}'s first recorded deposit. It settles everything collected so far; cash in hand is tracked from here on.
            </p>
          )}
          <div className="form-group">
            <label className="form-label" htmlFor="dep-amount">Amount handed over (₹)</label>
            <input id="dep-amount" className="form-control" type="number" min="1" step="1" required autoFocus value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" htmlFor="dep-notes">Notes (optional)</label>
            <input id="dep-notes" className="form-control" maxLength={300} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          {recent.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div className="form-label">Recent deposits</div>
              {recent.slice(0, 5).map((d) => (
                <div key={d._id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderTop: '1px solid var(--border)', fontSize: '.82rem' }}>
                  <span style={{ flex: 1 }}>{inr(d.amount)} · {new Date(d.depositedAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}{d.receivedBy?.name ? ` · by ${d.receivedBy.name}` : ''}</span>
                  {confirmId === d._id
                    ? <><button type="button" className="btn btn-danger btn-sm" onClick={() => remove(d._id)}>Remove it</button><button type="button" className="btn btn-secondary btn-sm" onClick={() => setConfirmId(null)}>Keep</button></>
                    : <button type="button" className="btn btn-secondary btn-sm" onClick={() => setConfirmId(d._id)}>Remove</button>}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving || !(Number(amount) > 0)}>{saving ? 'Saving…' : 'Record deposit'}</button>
        </div>
      </form>
    </div>
  );
}

export default function InsightsPage() {
  useDarkReady();
  const navigate = useNavigate();
  const [service, setService] = useState('all');
  const [overview, reloadOverview] = useLoad(() => insightsAPI.overview(service), [service]);
  const [agents, reloadAgents] = useLoad(() => insightsAPI.agents(), []);
  const [network, reloadNetwork] = useLoad((fresh) => insightsAPI.network(fresh), []);
  const [depositFor, setDepositFor] = useState(null);

  const o = overview.data;
  const a = agents.data;
  const n = network.data;
  const refresh = () => { reloadOverview(); reloadAgents(); reloadNetwork(true); };
  const usual = o?.collection.weekdayAvgSoFar;
  const todayVsUsual = o && usual > 0 ? Math.round(((o.collection.today - usual) / usual) * 100) : null;
  // A tile whose section failed shows a dash and says so, instead of "…" forever.
  const tileValue = (st, fmt) => (st.data ? fmt(st.data) : st.error ? '—' : '…');
  const failed = (st) => (st.error ? "couldn't load" : null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div className="flex items-center justify-between" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ fontSize: '1.3rem', fontWeight: 700 }}>Insights</h2>
          <div className="text-muted text-small">What needs attention today{o ? ` · ${dayLabel(o.today)}` : ''}</div>
        </div>
        <div className="flex items-center gap-2">
          <div role="group" aria-label="Service" style={{ display: 'inline-flex', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: 3 }}>
            {SERVICE_TABS.map(([v, l]) => (
              <button key={v} type="button" aria-pressed={service === v} onClick={() => setService(v)}
                style={{ border: 0, font: 'inherit', fontSize: '.82rem', padding: '5px 12px', borderRadius: 8, cursor: 'pointer',
                  background: service === v ? 'var(--primary-light)' : 'transparent', color: service === v ? 'var(--accent-text, var(--primary))' : 'var(--text-muted)', fontWeight: service === v ? 600 : 500 }}>
                {l}
              </button>
            ))}
          </div>
          <button type="button" className="btn btn-secondary btn-sm" onClick={refresh}><RefreshCw size={14} /> Refresh</button>
        </div>
      </div>

      <div className="dashboard-stat-grid" style={{ gap: 12 }}>
        <Tile icon={TrendingUp} label="Today's collection" value={tileValue(overview, (d) => inr(d.collection.today))}
          foot={failed(overview) || (todayVsUsual == null ? 'nothing usual by this time yet' : <span style={{ color: todayVsUsual >= 0 ? 'var(--success)' : 'var(--danger)' }}>{todayVsUsual >= 0 ? '▲' : '▼'} {Math.abs(todayVsUsual)}% vs usual by now</span>)} />
        <Tile icon={Wallet} label="Due next 7 days" value={tileValue(overview, (d) => inr(d.due7.amount))} foot={failed(overview) || (o ? `${o.due7.count} renewals` : '')} />
        <Tile icon={AlertTriangle} label="At-risk revenue" value={tileValue(overview, (d) => inr(d.atRisk.amount))} foot={failed(overview) || (o ? `${o.atRisk.count} expired ≤15 days` : '')} />
        <Tile icon={Users} label="Cash with agents" value={tileValue(agents, (d) => inr(d.cashWithAgents))} foot={failed(agents) || 'agents with a recorded deposit'} />
        <Tile icon={Activity} label="Network today" value={tileValue(network, (d) => `${d.outages.length} outage${d.outages.length === 1 ? '' : 's'}`)} foot={failed(network) || (n ? `${n.signal.length} ONUs losing signal` : '')} />
      </div>

      <div className="dashboard-grid-2">
        <Section title="Cash flow" hint="Solid bars: money collected each day. Light bars: plan value falling due, from each customer's expiry date and plan price.">
          <StateLine state={overview}>
            {o && (
              <div style={{ height: 240 }}>
                <ResponsiveContainer>
                  <BarChart data={o.flow} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
                    <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="day" tickFormatter={dayLabel} interval={3} tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={inrK} width={48} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v, _n, p) => [inr(v), p.payload.kind === 'collected' ? 'Collected' : 'Expected']} labelFormatter={dayLabel} />
                    <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                      {o.flow.map((f) => <Cell key={`${f.kind}-${f.day}`} fill="var(--primary)" fillOpacity={f.kind === 'collected' ? 1 : 0.35} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </StateLine>
        </Section>

        <Section title="At-risk revenue" hint="Expired in the last 15 days and not renewed. Biggest first.">
          <StateLine state={overview} empty={o && o.atRisk.count === 0 ? 'Nobody has expired in the last 15 days.' : null}>
            {o && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12 }}>
                  {['1-3', '4-7', '8-15'].map((b) => (
                    <div key={b} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '8px 10px' }}>
                      <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>{b} days</div>
                      <div style={{ fontWeight: 700 }}>{inr(o.atRisk.buckets[b].amount)}</div>
                      <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>{o.atRisk.buckets[b].count} customers</div>
                    </div>
                  ))}
                </div>
                <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                  {o.atRisk.top.map((r) => (
                    <div key={`${r.id}-${r.service}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: '1px solid var(--border)' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '.85rem' }}>{r.name}</div>
                        <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>
                          {r.service === 'bb' ? 'Broadband' : 'Cable TV'} · {r.plan || 'no plan'}{r.area ? ` · ${r.area}` : ''}{r.collector ? ` · ${r.collector}` : ''}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: 600, fontSize: '.85rem' }}>{inr(r.amount)}</div>
                        <span className={`badge ${r.daysExpired <= 3 ? 'badge-info' : r.daysExpired <= 7 ? 'badge-warning' : 'badge-danger'}`}>{r.daysExpired}d expired</span>
                      </div>
                      <button type="button" className="btn btn-outline btn-sm" onClick={() => navigate(`/customers/${r.id}`)}>Open</button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </StateLine>
        </Section>
      </div>

      <Section title={`Agent scorecard${a ? ` · ${new Date(`${a.month}-01T00:00:00`).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}` : ''}`}
        hint="Collections this month by who collected them. Failed visits come from the agent app. Cash in hand starts counting from an agent's first recorded deposit.">
        <StateLine state={agents} empty={a && a.rows.length === 0 ? 'No collections recorded this month yet.' : null}>
          {a && (
            <div className="table-container">
              <table>
                <thead><tr><th>Collector</th><th style={{ textAlign: 'right' }}>Collected</th><th style={{ textAlign: 'right' }}>Cash</th><th style={{ textAlign: 'right' }}>Online</th><th style={{ textAlign: 'right' }}>Payments</th><th style={{ textAlign: 'right' }}>Failed visits</th><th>Success</th><th style={{ textAlign: 'right' }}>Cash in hand</th><th>Last deposit</th><th /></tr></thead>
                <tbody>
                  {a.rows.map((r) => (
                    <tr key={r.agent.id}>
                      <td><b style={{ fontWeight: 600 }}>{r.agent.name}</b><div style={{ fontSize: '.72rem', color: 'var(--text-muted)', textTransform: 'capitalize' }}>{r.agent.role}</div></td>
                      <td style={{ textAlign: 'right' }}>{inr(r.collected)}</td>
                      <td style={{ textAlign: 'right' }}>{inr(r.cash)}</td>
                      <td style={{ textAlign: 'right' }}>{inr(r.online)}</td>
                      <td style={{ textAlign: 'right' }}>{r.payments}</td>
                      <td style={{ textAlign: 'right' }}>{r.failed}</td>
                      <td>{r.successRate == null ? '—' : (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ width: 70, height: 6, borderRadius: 3, background: 'var(--border)', display: 'inline-block', position: 'relative' }}>
                            <span style={{ position: 'absolute', inset: 0, width: `${r.successRate}%`, borderRadius: 3, background: 'var(--primary)' }} />
                          </span>{r.successRate}%
                        </span>)}
                      </td>
                      <td style={{ textAlign: 'right' }}>{r.cashInHand == null ? <span style={{ color: 'var(--text-muted)' }}>Not tracked yet</span>
                        : <b style={{ color: r.cashInHand < 0 ? 'var(--danger)' : 'var(--text)', fontWeight: 600 }}>{inr(r.cashInHand)}</b>}</td>
                      <td style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>{r.lastDeposit ? `${inr(r.lastDeposit.amount)} · ${new Date(r.lastDeposit.at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}` : '—'}</td>
                      <td><button type="button" className="btn btn-secondary btn-sm" onClick={() => setDepositFor(r)}>Record deposit</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </StateLine>
      </Section>

      <div className="dashboard-grid-2">
        <Section title="Signal dropping" hint="ONUs whose optical power fell over the last 7 days. Below −27 dBm the line starts dropping.">
          <StateLine state={network} empty={n && n.signal.length === 0 ? 'No ONUs losing signal.' : null}>
            {n && (
              <div className="table-container">
                <table>
                  <thead><tr><th>Customer</th><th>7-day trend</th><th style={{ textAlign: 'right' }}>Now</th><th style={{ textAlign: 'right' }}>Change</th><th /></tr></thead>
                  <tbody>
                    {n.signal.map((sg) => (
                      <tr key={sg.serial}>
                        <td>
                          {sg.customer ? <button type="button" onClick={() => navigate(`/customers/${sg.customer.id}`)} style={{ background: 'none', border: 0, padding: 0, font: 'inherit', fontWeight: 600, textAlign: 'left', color: 'var(--accent-text, var(--primary))', cursor: 'pointer' }}>{sg.customer.name}</button>
                            : <b style={{ fontWeight: 600 }}>Not linked to a customer</b>}
                          <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>{sg.customer?.area ? `${sg.customer.area} · ` : ''}<span className="font-mono">{sg.onuId || sg.serial}</span></div>
                        </td>
                        <td><Spark points={sg.points} /></td>
                        <td style={{ textAlign: 'right' }} className="font-mono">{sg.latest.toFixed(1)} dBm</td>
                        <td style={{ textAlign: 'right', color: sg.drop < 0 ? 'var(--danger)' : 'var(--text-muted)' }} className="font-mono">{sg.drop > 0 ? '+' : ''}{sg.drop.toFixed(1)}</td>
                        <td><span className={`badge ${SIGNAL_PILL[sg.state][0]}`}>{SIGNAL_PILL[sg.state][1]}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </StateLine>
        </Section>

        <Section title="Outages today" hint="PON ports, the OLT and the uplink going down, and how many customers each affects."
          right={n && <span className="text-muted text-small">This week: {n.week.count} · {mins(n.week.minutes)}</span>}>
          <StateLine state={network} empty={n && n.outages.length === 0 ? 'No outages today.' : null}>
            {n && n.outages.map((ot, i) => {
              // Drawn on today's timeline; an outage carried over from yesterday starts at the left edge.
              const dayStart = new Date(); dayStart.setHours(0, 0, 0, 0);
              const pct = (t) => Math.max(0, Math.min(100, ((new Date(t) - dayStart) / 864e5) * 100));
              return (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 10, alignItems: 'center', padding: '6px 0' }}>
                  <div style={{ fontSize: '.8rem' }}>
                    <b style={{ fontWeight: 600 }}>{ot.source}</b>
                    <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>{OUTAGE_LABEL[ot.type]} · {ot.customers} customers</div>
                  </div>
                  <div title={`${timeLabel(ot.start)} – ${ot.ongoing ? 'now' : timeLabel(ot.end)} (${mins(ot.minutes)})`}
                    style={{ position: 'relative', height: 22, background: 'var(--surface-hover, var(--border))', borderRadius: 6 }}>
                    <div style={{ position: 'absolute', top: 3, bottom: 3, left: `${pct(ot.start)}%`, width: `${Math.max(0.6, pct(ot.end) - pct(ot.start))}%`, background: ot.ongoing ? 'var(--danger)' : 'var(--warning)', borderRadius: 4 }} />
                    <span style={{ position: 'absolute', right: 6, top: 3, fontSize: '.7rem', color: 'var(--text-muted)' }}>{timeLabel(ot.start)} · {ot.ongoing ? 'ongoing' : mins(ot.minutes)}</span>
                  </div>
                </div>
              );
            })}
          </StateLine>
        </Section>
      </div>

      {depositFor && <DepositModal row={depositFor} onClose={() => setDepositFor(null)} onSaved={(close = true) => { if (close) setDepositFor(null); reloadAgents(); }} />}
    </div>
  );
}
